import { REPUB_COUNTRY, type RequestContext } from '@cx-web/domain-types';
import { BETTING_SPLITS_CONFIGS, CATEGORY_ORDERING_CONFIGS, REPUB_CONFIG } from '@cx-web/repub-config';
import { getRepubMetaFromRepub, isOcPlusRepub } from '@cx-web/utils';
import { Comparison } from '@fpsm/cxy-components/Comparison';
import memoize from 'lodash.memoize';
import format from 'string-format';

import { REPUBS } from '@cx-modules/constants';

import { Avatar } from '~/apps/product-plus/components/avatar/Avatar';
import { BetButton } from '~/apps/product-plus/components/bet-button/BetButton';
import { BettingSplits, styles } from '~/apps/product-plus/components/BettingInsights/components/BettingSplits';
import {
  BetTypeFilter,
  CheckboxFilter,
  DateRefinement,
} from '~/apps/product-plus/components/BettingInsights/components/Filters';
import { Autocomplete } from '~/apps/product-plus/components/BettingInsights/components/Search';
import {
  marketMeta,
  marketMetaIt,
  Moneyline,
  PointSpread,
  TotalPoints,
  TotalRuns,
  WinMarket,
} from '~/apps/product-plus/components/BettingInsights/constants';
import { trackBettingSplitsBetClick } from '~/apps/product-plus/components/BettingInsights/tracking';
import { isGzRepub } from '~/apps/product-plus/components/BettingInsights/types';
import {
  type BettingInsightsConfigFactoryOptions,
  type BettingSplitsBet,
  type BettingSplitsIndex,
  type BettingSplitsMarket,
} from '~/apps/product-plus/configuration/types';
import {
  type BettingInsightsUIConfig,
  createBettingInsightsUIConfig,
} from '~/apps/product-plus/configuration/ui/BettingInsightsUI';
import { formatLine } from '~/apps/product-plus/configuration/utils/formatLine';
import { getPercentageComparisonValues } from '~/apps/product-plus/configuration/utils/getPercentageComparisonValues';
import { sortBetsBySequence } from '~/apps/product-plus/configuration/utils/sortBetsBySequence';
import { FILTERS_CONFIGS } from '~/apps/product-plus/search-domain/config/filters-config';
import {
  EVENT_DISPLAY_NAME,
  getEventDisplayNames,
} from '~/apps/product-plus/search-domain/constants/eventDisplayNames';
import { bettingSplitsIndex } from '~/apps/product-plus/search-domain/indexes';
import { getMatchupTeamInfo } from '~/apps/product-plus/utils/getMatchupTeamInfo';
import { featureFlags } from '~/providers/feature-flag-provider';

export function createBettingSplitsUIConfig(
  requestContext: RequestContext,
  _options: BettingInsightsConfigFactoryOptions = {},
  tags: string[],
) {
  const translations = FILTERS_CONFIGS[requestContext.repubConfig.name as keyof typeof FILTERS_CONFIGS];

  const { repubCountry: factoryRepubCountry } = getRepubMetaFromRepub(requestContext.repubConfig.name);

  const getBetButtonLabel = (bet: BettingSplitsBet, marketInfo: string) => {
    return marketInfo
      ? format(translations?.bestMarket, {
          market: marketInfo,
        })
      : format(translations.bestLine, {
          line: bet.line?.name,
        });
  };

  const uiConfig: BettingInsightsUIConfig<BettingSplitsIndex, BettingSplitsMarket> = {
    card: {
      styles,
      table: {
        body: {
          render(_context, { bookmakers, content, resultIndex, sectionProps, table }) {
            if (!content.bets) return null;

            const isFootball = content.categoryGroupName === 'FOOTBALL';

            return (
              <BettingSplits.Body
                bookmakers={bookmakers}
                // @ts-expect-error - TypeScript structural typing false positive: factory-created cells type is structurally identical but seen as unrelated due to module boundary inference. See docs/refactoring-config-to-functional.md for details.
                cells={table.cells}
                content={content}
                isFootball={isFootball}
                resultIndex={resultIndex}
                sectionProps={sectionProps}
              />
            );
          },
        },
        getContent(context, { result }) {
          const repub = context.repubConfig.name;
          if (!isOcPlusRepub(repub)) {
            return [];
          }

          const repubCountry = REPUB_COUNTRY[repub];
          const marketOrder = [PointSpread, Moneyline, WinMarket, TotalPoints, TotalRuns];
          return result.markets.sort(
            (a, b) =>
              marketOrder.indexOf(a.marketDisplayNameMap[repubCountry] ?? a.marketDisplayNameMap.us) -
              marketOrder.indexOf(b.marketDisplayNameMap[repubCountry] ?? b.marketDisplayNameMap.us),
          );
        },
        headings: {
          content: [
            {
              key: 'left',
              render(_context, { content }) {
                const { bets, categoryGroupName, marketClassification, marketGenericSequence } = content;
                const [leftBet] = sortBetsBySequence(bets, marketClassification, marketGenericSequence);
                const isSoccer = categoryGroupName === 'FOOTBALL';

                return (
                  <>
                    {content.teamData?.home?.badgeUrl && content.teamData?.away?.badgeUrl && (
                      <Avatar
                        betName={leftBet.betName}
                        homeOrAway={isSoccer ? 'home' : 'away'}
                        marketClassification={marketClassification}
                        marketPropType={content.marketPropType}
                        teamData={content.teamData}
                      />
                    )}
                    <p data-testid="left-bet" style={{ fontSize: 'inherit' }}>
                      {leftBet.betName}
                    </p>
                  </>
                );
              },
              renderMobile(_context, { content }) {
                const { bets, categoryGroupName, marketClassification, marketGenericSequence, teamData } = content;
                const [leftBet] = sortBetsBySequence(bets, marketClassification, marketGenericSequence);
                const isSoccer = categoryGroupName === 'FOOTBALL';
                const mobileName =
                  (teamData && leftBet.teamType && teamData[leftBet.teamType]?.shortName) || leftBet.betName;

                return (
                  <>
                    {content.teamData?.home?.badgeUrl && content.teamData?.away?.badgeUrl && (
                      <Avatar
                        betName={content.bets[0].betName}
                        homeOrAway={isSoccer ? 'home' : 'away'}
                        marketClassification={marketClassification}
                        marketPropType={content.marketPropType}
                        teamData={content.teamData}
                      />
                    )}
                    <p data-testid="left-bet-mobile" style={{ fontSize: 'inherit' }}>
                      {mobileName}
                    </p>
                  </>
                );
              },
            },
            {
              key: 'title',
              render(context, { content }) {
                const repub = context.repubConfig.name;
                if (!isOcPlusRepub(repub)) {
                  return null;
                }

                const repubCountry = REPUB_COUNTRY[repub];
                return (
                  <p className="mainHeading" data-testid="market-header">
                    {/* @ts-ignore */}
                    {marketMeta[content.marketDisplayNameMap[repubCountry]]?.heading ||
                      `${content.marketDisplayNameMap[repubCountry]} (O/U)`}
                  </p>
                );
              },
              isMain: true,
              justifyContent: 'center',
              shouldShowIndicator: true,
            },
            {
              key: 'right',
              render(_context, { content }) {
                const { bets, categoryGroupName, marketClassification, marketGenericSequence } = content;
                const [_leftBet, _, rightBet] = sortBetsBySequence(bets, marketClassification, marketGenericSequence);

                // Handle case where rightBet is undefined (e.g., single total bet)
                if (!rightBet) {
                  return null;
                }

                const isSoccer = categoryGroupName === 'FOOTBALL';

                return (
                  <>
                    <p data-testid="right-bet" style={{ fontSize: 'inherit' }}>
                      {rightBet.betName}
                    </p>
                    {content.teamData?.home?.badgeUrl && content.teamData?.away?.badgeUrl && (
                      <Avatar
                        betName={content.bets[0].betName}
                        homeOrAway={isSoccer ? 'away' : 'home'}
                        marketClassification={marketClassification}
                        marketPropType={content.marketPropType}
                        teamData={content.teamData}
                      />
                    )}
                  </>
                );
              },
              renderMobile(_context, { content }) {
                const { bets, categoryGroupName, marketClassification, marketGenericSequence, teamData } = content;
                const [_leftBet, _, rightBet] = sortBetsBySequence(bets, marketClassification, marketGenericSequence);

                // Handle case where rightBet is undefined (e.g., single total bet)
                if (!rightBet) {
                  return null;
                }

                const isSoccer = categoryGroupName === 'FOOTBALL';
                const mobileName =
                  (teamData && rightBet.teamType && teamData[rightBet.teamType]?.shortName) || rightBet.betName;
                return (
                  <>
                    <p data-testid="right-bet-mobile" style={{ fontSize: 'inherit', textAlign: 'end' }}>
                      {mobileName}
                    </p>
                    {content.teamData?.home?.badgeUrl && content.teamData?.away?.badgeUrl && (
                      <Avatar
                        betName={content.bets[0].betName}
                        homeOrAway={isSoccer ? 'away' : 'home'}
                        marketClassification={marketClassification}
                        marketPropType={content.marketPropType}
                        teamData={content.teamData}
                      />
                    )}
                  </>
                );
              },
              justifyContent: 'flex-end',
            },
          ],
        },
        cells: [
          {
            key: 'left',
            render(
              context,
              {
                bookmakers,
                content: {
                  bets,
                  categoryName,
                  marketClassification,
                  marketDisplayNameMap,
                  marketGenericSequence,
                  marketPropType,
                  subeventName,
                  teamData,
                },
              },
            ) {
              const repub = context.repubConfig.name;
              if (!isOcPlusRepub(repub)) {
                return null;
              }

              const repubCountry = REPUB_COUNTRY[repub];
              const sortBets = memoize(() => sortBetsBySequence(bets, marketClassification, marketGenericSequence));
              const gameBets = sortBets();
              const [leftBet, middleBet] = gameBets;

              const bookmaker = bookmakers[leftBet.bookmakerCode];
              if (!bookmaker) {
                return null;
              }

              const hasThreeWayResult = middleBet !== undefined;

              const marketMetaToUse = isGzRepub(repub) || repub === REPUBS.IT.name ? marketMetaIt : marketMeta;

              const marketDisplayName =
                (marketDisplayNameMap[repubCountry] as keyof typeof marketMetaToUse) ??
                (marketDisplayNameMap.us as keyof typeof marketMetaToUse);

              const line = hasThreeWayResult ? teamData?.home?.shortName : leftBet.line && formatLine(leftBet.line);
              const onClick = memoize(() => {
                trackBettingSplitsBetClick({
                  bet: leftBet,
                  bookmaker,
                  categoryName,
                  component: 'Betting Splits',
                  marketDisplayName,
                  marketPropType,
                  subeventName,
                });
              });

              const hideBookieLogo = repub === REPUBS.WS.name || repub === REPUBS.GZ.name;

              return (
                <BetButton
                  aria-label={`Place a bet on ${leftBet.betName} ${marketDisplayName} with ${bookmaker.bookmakerName} at odds of ${leftBet.oddsAmerican}`}
                  bet={leftBet}
                  bookieColour={bookmaker?.brandColour || 'black'}
                  bookieLogo={bookmaker?.logoSmall}
                  bookieName={bookmaker?.bookmakerName}
                  buttonStyles={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                  hideBookieLogo={hideBookieLogo}
                  label={hasThreeWayResult ? '' : getBetButtonLabel(leftBet, marketMetaToUse[marketDisplayName]?.info)}
                  line={line ?? undefined}
                  onClick={onClick}
                  shouldHideLabelOnDesktop={hasThreeWayResult}
                  shouldHideLabelOnMobile
                  unformattedOdds={leftBet}
                />
              );
            },
          },
          {
            key: 'comparison',
            render(context, { content: { bets, marketClassification, marketDisplayNameMap, marketGenericSequence } }) {
              const repub = context.repubConfig.name;
              if (!isOcPlusRepub(repub)) {
                return null;
              }

              const repubCountry = REPUB_COUNTRY[repub];
              const sortBets = memoize(() => sortBetsBySequence(bets, marketClassification, marketGenericSequence));
              const [leftBet, centerBet, rightBet] = sortBets();

              // Handle case where rightBet is undefined (e.g., single total bet)
              if (!rightBet) {
                return <p style={{ textAlign: 'center', fontSize: 'inherit' }}>{leftBet.betName} only</p>;
              }

              const marketDisplayName = marketDisplayNameMap[repubCountry] ?? marketDisplayNameMap.us;
              const betInformation = `% of ${marketDisplayName}, ${leftBet.betName} v ${rightBet.betName},`;

              const bettingSplitsConfig = BETTING_SPLITS_CONFIGS[repub];
              const comparisonBetsLabel = bettingSplitsConfig.comparisonBetsLabel(marketDisplayName);
              const comparisonMoneyLabel = bettingSplitsConfig.comparisonMoneyLabel(marketDisplayName);
              const getPercentageComparison = memoize(() =>
                getPercentageComparisonValues(
                  leftBet.betPercentage,
                  rightBet.betPercentage,
                  centerBet?.betPercentage || undefined,
                ),
              );
              const betValues = getPercentageComparison();

              const getStakeComparison = memoize(() =>
                getPercentageComparisonValues(
                  leftBet.stakePercentage,
                  rightBet.stakePercentage,
                  centerBet?.stakePercentage || undefined,
                ),
              );
              const stakeValues = getStakeComparison();

              const marketMetaToUse = isGzRepub(repub) || repub === REPUBS.IT.name ? marketMetaIt : marketMeta;

              return (
                <>
                  <Comparison
                    aria-label={`${betInformation} Bets with ${betValues.leftValue}% on the left and ${betValues.rightValue}% on the right.`}
                    comparisonLabel={comparisonBetsLabel}
                    middleText={translations.middleBetLine}
                    // @ts-ignore
                    tooltipText={marketMetaToUse[marketDisplayName]?.betsTooltipText}
                    {...betValues}
                  />
                  <Comparison
                    aria-label={`${betInformation} stakes with ${stakeValues.leftValue}% on the left and ${stakeValues.rightValue}% on the right.`}
                    comparisonLabel={comparisonMoneyLabel}
                    middleText={translations.middleBetLine}
                    // @ts-ignore
                    tooltipText={marketMetaToUse[marketDisplayName]?.moneyTooltipText}
                    {...stakeValues}
                  />
                </>
              );
            },
            isMain: true,
            isFullWidthMobile: true,
            order: -1,
            justifyContent: 'center',
          },
          {
            key: 'right',
            render(
              context,
              {
                bookmakers,
                content: {
                  bets,
                  categoryName,
                  marketClassification,
                  marketDisplayNameMap,
                  marketGenericSequence,
                  marketPropType,
                  subeventName,
                  teamData,
                },
              },
            ) {
              const repub = context.repubConfig.name;
              if (!isOcPlusRepub(repub)) {
                return null;
              }

              const repubCountry = REPUB_COUNTRY[repub];
              const sortBets = memoize(() => sortBetsBySequence(bets, marketClassification, marketGenericSequence));
              const gameBets = sortBets();
              const [_leftBet, middleBet, rightBet] = gameBets;

              // Handle case where rightBet is undefined (e.g., single total bet)
              if (!rightBet) {
                return null;
              }

              const hasThreeWayResult = middleBet !== undefined;

              const line = hasThreeWayResult ? teamData?.away?.shortName : rightBet.line && formatLine(rightBet.line);

              const bookmaker = bookmakers[rightBet.bookmakerCode];
              if (!bookmaker) {
                return null;
              }

              const hideBookieLogo = repub === REPUBS.WS.name || repub === REPUBS.GZ.name;

              const marketMetaToUse = isGzRepub(repub) || repub === REPUBS.IT.name ? marketMetaIt : marketMeta;
              const marketDisplayName =
                (marketDisplayNameMap[repubCountry] as keyof typeof marketMetaToUse) ??
                (marketDisplayNameMap.us as keyof typeof marketMetaToUse);

              const onClick = memoize(() => {
                trackBettingSplitsBetClick({
                  bet: rightBet,
                  bookmaker,
                  categoryName,
                  component: 'Betting Splits',
                  marketDisplayName,
                  marketPropType,
                  subeventName,
                });
              });

              return (
                <BetButton
                  aria-label={`Place a bet on ${rightBet.betName} ${marketDisplayName} with ${bookmaker.bookmakerName} at odds of ${rightBet.oddsAmerican}`}
                  bet={rightBet}
                  bookieColour={bookmaker?.brandColour || 'black'}
                  bookieLogo={bookmaker?.logoSmall}
                  bookieName={bookmaker?.bookmakerName}
                  buttonStyles={{
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                  }}
                  hideBookieLogo={hideBookieLogo}
                  label={
                    hasThreeWayResult
                      ? ''
                      : // @ts-ignore
                        getBetButtonLabel(rightBet, marketMetaToUse[marketDisplayName]?.info)
                  }
                  line={line ?? undefined}
                  onClick={onClick}
                  shouldHideLabelOnDesktop={hasThreeWayResult}
                  shouldHideLabelOnMobile
                  unformattedOdds={rightBet}
                />
              );
            },
            start: 3,
            justifyContent: 'flex-end',
          },
          {
            key: 'center',
            render(
              context,
              {
                bookmakers,
                content: {
                  bets,
                  categoryName,
                  marketClassification,
                  marketDisplayNameMap,
                  marketGenericSequence,
                  marketPropType,
                  subeventName,
                },
              },
            ) {
              const repub = context.repubConfig.name;
              if (!isOcPlusRepub(repub)) {
                return null;
              }

              const repubCountry = REPUB_COUNTRY[repub];
              const sortBets = memoize(() => sortBetsBySequence(bets, marketClassification, marketGenericSequence));
              const [_, centerBet] = sortBets();

              const marketDisplayName = marketDisplayNameMap[repubCountry] ?? marketDisplayNameMap.us;

              const hideBookieLogo = repub === REPUBS.WS.name || repub === REPUBS.GZ.name;

              if (!centerBet) {
                return null;
              }

              const bookmaker = bookmakers[centerBet.bookmakerCode];
              if (!bookmaker) {
                return null;
              }

              const onClick = memoize(() => {
                trackBettingSplitsBetClick({
                  bet: centerBet,
                  bookmaker,
                  categoryName,
                  component: 'Betting Splits',
                  marketDisplayName,
                  marketPropType,
                  subeventName,
                });
              });
              return (
                <BetButton
                  aria-label={`Place a bet on ${centerBet.betName} ${marketDisplayName} with ${bookmaker.bookmakerName} at odds of ${centerBet.oddsAmerican}`}
                  bet={centerBet}
                  bookieColour={bookmaker?.brandColour || 'black'}
                  bookieLogo={bookmaker?.logoSmall}
                  bookieName={bookmaker?.bookmakerName}
                  buttonStyles={{
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                  hideBookieLogo={hideBookieLogo}
                  line={translations.middleBetLine}
                  onClick={onClick}
                  shouldHideLabelOnDesktop
                  shouldHideLabelOnMobile
                  unformattedOdds={centerBet}
                />
              );
            },
            justifyContent: 'center',
          },
        ],
        shouldToggle: true,
        showAdditionalTitle: true,
      },
      title: {
        format: ({ result }) => {
          const { categoryGroupName, subeventName, teamData } = result;
          const { matchupString } = getMatchupTeamInfo({ categoryGroupName, subeventName, teamData });

          return `${matchupString} | {subeventStartTime}`;
        },
      },
      wrapper: (_context, { children, config, content, result }) => (
        <BettingSplits config={config} content={content} result={result}>
          {children}
        </BettingSplits>
      ),
    },
    initialUiState(context) {
      const soccerBettingSplitsEnabled =
        context.featureFlags.find(flag => flag.name === featureFlags.SUBS__PLUS__SOCCER_BETTING_SPLITS)?.enabled ??
        false;

      const plusConfig = REPUB_CONFIG[context.repubConfig.name]?.plus;
      const defaultEventDisplayName = plusConfig?.bettingSplits.defaultEventDisplayName ?? 'NBA';

      // If soccer betting splits is disabled and the default is EPL, fall back to NBA
      const eventDisplayName =
        !soccerBettingSplitsEnabled && defaultEventDisplayName === 'EPL' ? 'NBA' : defaultEventDisplayName;

      return {
        hitsPerPage: 10,
        refinementList: {
          eventDisplayName: [eventDisplayName],
        },
      };
    },
    refinements: {
      clearAll: {
        excludedAttributes: [],
      },
      components: [
        {
          attribute: 'bookmakerCode',
          getLabel(_context) {
            return 'Bookmaker (Internal use only)';
          },
          render(context, { bookmakers }) {
            const areInternalSearchToolsEnabled =
              context.featureFlags.find(flag => flag.name === featureFlags.SUBS__PLUS__INTERNAL_SEARCH_TOOLS)
                ?.enabled ?? false;

            if (!areInternalSearchToolsEnabled) {
              return null;
            }

            return (
              <CheckboxFilter
                attribute={context.attribute}
                isDisabled={context.isWalled}
                label="Bookmaker (Internal use only)"
                repubCountry={factoryRepubCountry}
                sortBy={['name:asc']}
                transformFieldLabel={({ count, label }) => {
                  const bookmaker = bookmakers[label];
                  return `${bookmaker?.bookmakerName ?? label} (${count})`;
                }}
              />
            );
          },
        },
        {
          attribute: 'marketDisplayName',
          getLabel(_context) {
            return translations.betType;
          },
          render(context) {
            return (
              <BetTypeFilter
                attribute={context.attribute}
                isDisabled={context.isWalled}
                label="Bet Type"
                sortBy={['name:asc']}
              />
            );
          },
        },
        {
          attribute: 'subeventStartDate',
          getLabel(_context) {
            return translations.date;
          },
          render(context) {
            return (
              <DateRefinement
                attribute={context.attribute}
                isDisabled={context.isWalled}
                label="Dates"
                repubCountry={factoryRepubCountry}
              />
            );
          },
        },
      ],
    },
    results: {
      categories(context) {
        const isSoccerBettingSplitsEnabled =
          context.featureFlags.find(flag => flag.name === featureFlags.SUBS__PLUS__SOCCER_BETTING_SPLITS)?.enabled ??
          false;

        const repubName = context.repubConfig.name;
        const baseCategoryOrdering =
          CATEGORY_ORDERING_CONFIGS[repubName as keyof typeof CATEGORY_ORDERING_CONFIGS] ??
          CATEGORY_ORDERING_CONFIGS.OC;

        // For US republic or when soccer is disabled, only use the start ordering (no end)
        const categoryOrdering =
          repubName === REPUBS.US.name || !isSoccerBettingSplitsEnabled
            ? { start: baseCategoryOrdering.start }
            : baseCategoryOrdering;

        return {
          attribute: EVENT_DISPLAY_NAME,
          labels: getEventDisplayNames(repubName),
          order: categoryOrdering,
        };
      },
      propsName: 'games',
      labelledBy: '#bet-name',
    },
    search: {
      render(context, { searchAttributes }) {
        const textSearchEnabled =
          context.featureFlags.find(f => f.name === featureFlags.SUBS__PLUS__TEXT_SEARCH)?.enabled ?? false;

        if (!textSearchEnabled) {
          return null;
        }

        return (
          <Autocomplete
            disabled={context.isWalled}
            indexName={bettingSplitsIndex}
            repub={context.repubConfig.name}
            searchAttributes={searchAttributes}
          />
        );
      },
    },
    sorting: {
      canSort({ refinements }) {
        return refinements.filter(r => r.attribute === 'marketDisplayName').length > 0;
      },
    },
  };

  return { ui: createBettingInsightsUIConfig(requestContext, uiConfig, tags) };
}
